import { useMemo, type ComponentProps } from 'react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import {
  SecureScalePages,
  createInvestigationClient,
  createAuditFindingsClient,
  createDashboardClient,
  createSettingsClient,
  createCommissioningClient,
  createCustodyClient,
  createStorageConfigClient,
  type SecureScaleManagerClient,
} from '@securescale/admin-pages';
import { ReportingSettings } from '@reform-lite/contracts';
import { useAuthStore } from '@/auth/auth-store';
import { apiClient } from '@/lib/api-client';
import { useT } from '@/i18n/use-t';
import { useLocaleStore } from '@/i18n/store';
import { securescaleClient } from './securescale-client';

/** Authenticated BOS module. Private session cache prevents cross-session
 * tenant/user evidence reuse from the package's legacy unscoped query keys. */
export function SecureScaleRoute({
  client = securescaleClient,
}: {
  client?: SecureScaleManagerClient;
}) {
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.accessToken);
  const locale = useLocaleStore((s) => s.locale);
  const t = useT('admin_ui');
  const entitled = user?.modules?.includes('securescale') === true;
  const session = useMemo(
    () => ({
      key: crypto.randomUUID(),
      queries: new QueryClient({ defaultOptions: { queries: { retry: false } } }),
      investigations: createInvestigationClient(apiClient),
      findings: createAuditFindingsClient(apiClient),
      dashboard: createDashboardClient(apiClient),
      settings: createSettingsClient(apiClient),
      commissioning: createCommissioningClient(apiClient),
      custody: createCustodyClient(apiClient),
      storageConfig: createStorageConfigClient(apiClient),
    }),
    [user?.id, token, entitled],
  );
  const permissions =
    user?.isSuperAdmin === true
      ? [
          'weight.view',
          'weight.manage',
          'weight.investigate',
          'weight.approve',
          'weight.resolveAttendant',
          'connectors.read',
          'locations.read',
          'terminal-params.write',
        ]
      : (user?.permissions ?? []);
  if (!entitled) return null;
  return (
    <QueryClientProvider client={session.queries}>
      <AuthenticatedSecureScalePages
        key={session.key}
        client={client}
        investigationClient={session.investigations}
        auditFindingsClient={session.findings}
        dashboardClient={session.dashboard}
        settingsClient={session.settings}
        commissioningClient={session.commissioning}
        custodyClient={session.custody}
        storageConfigClient={session.storageConfig}
        permissions={permissions}
        t={t}
        locale={locale}
      />
    </QueryClientProvider>
  );
}

/** Reporting metadata uses the same private session cache as weight evidence. */
function AuthenticatedSecureScalePages(props: ComponentProps<typeof SecureScalePages>) {
  const canRead =
    props.permissions?.includes('weight.view') === true ||
    props.permissions?.includes('weight.manage') === true;
  const reporting = useQuery({
    queryKey: ['securescale-reporting-settings'],
    enabled: canRead,
    queryFn: async () => {
      const { data } = await apiClient.get<{ reportingCurrency: string | null; revision: string }>(
        '/securescale/manage/reporting-settings',
      );
      return ReportingSettings.parse(data);
    },
  });
  const currency = reporting.data?.reportingCurrency;
  return (
    <SecureScalePages
      {...props}
      currency={currency && /^[A-Z]{3}$/.test(currency) ? currency : undefined}
    />
  );
}
